import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select an enquiry category');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contacts`, formData);
      
      toast.success('Message sent successfully!', {
        description: 'We will get back to you within 24 hours.'
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        category: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or call us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      value: companyInfo.phone,
      link: `tel:${companyInfo.phone}`,
      color: "text-emerald-600",
      bg: "bg-emerald-100"
    },
    {
      icon: Mail,
      title: "Email",
      value: companyInfo.email,
      link: `mailto:${companyInfo.email}`,
      color: "text-blue-600",
      bg: "bg-blue-100"
    },
    {
      icon: MapPin,
      title: "Address",
      value: companyInfo.address,
      link: "https://maps.google.com/?q=A-1902,+Lashkaria+Pearl,+Behram+Baug,+Jogeswari+W+Mumbai+400102",
      color: "text-orange-600",
      bg: "bg-orange-100"
    },
    {
      icon: Clock,
      title: "Working Hours",
      value: "Mon - Sat: 9:00 AM - 6:00 PM",
      link: null,
      color: "text-purple-600",
      bg: "bg-purple-100"
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-700 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Contact Us</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-blue-200 leading-relaxed">
              Have questions about our programs? We're here to help you take the first step towards skill development.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 -mt-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className={`w-12 h-12 rounded-lg ${info.bg} flex items-center justify-center mb-4`}>
                  <info.icon className={`w-6 h-6 ${info.color}`} />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                {info.link ? (
                  <a
                    href={info.link}
                    target={info.title === 'Address' ? '_blank' : undefined}
                    rel={info.title === 'Address' ? 'noopener noreferrer' : undefined}
                    className="text-slate-600 text-sm hover:text-blue-700 transition-colors break-words"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-slate-600 text-sm">{info.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Send us a Message</h2>
              <p className="text-slate-600 mb-8">Fill out the form and our team will get back to you within 24 hours.</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Mobile number"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="category">Enquiry Category *</Label>
                  <Select onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="candidate">Candidate Enquiry</SelectItem>
                      <SelectItem value="establishment">Establishment Partnership</SelectItem>
                      <SelectItem value="general">General Enquiry</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="How can we help you?"
                    rows={5}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    required
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </div>

            {/* Map */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Our Location</h2>
              <p className="text-slate-600 mb-8">Visit us at our office in Mumbai.</p>
              
              <div className="bg-slate-100 rounded-2xl overflow-hidden shadow-lg h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.0012!2d72.8267!3d19.1477!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b63d98a79b15%3A0x8c6f67c8f3c0a64d!2sBehram%20Baug%2C%20Jogeshwari%20West%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1709123456789!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Park Skill Location"
                />
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">Park Skill Development Organization</p>
                    <p className="text-slate-600 text-sm">{companyInfo.address}</p>
                    <a
                      href="https://maps.google.com/?q=A-1902,+Lashkaria+Pearl,+Behram+Baug,+Jogeswari+W+Mumbai+400102"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-700 text-sm font-medium mt-2 hover:underline"
                    >
                      Get Directions
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-slate-600 mb-6">
            Register as a candidate or partner with us today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/careers">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Register as Candidate
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <a href="/apprenticeship">
              <Button variant="outline" className="border-blue-700 text-blue-700 hover:bg-blue-50">
                Partner with Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
